// eslint-disable-next-line no-unused-vars
import React, { memo, useState, useEffect, useCallback } from 'react';
import { useContextSelector } from 'use-context-selector';
import { useSWRConfig } from 'swr';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import { stringify } from 'query-string';
import { useForm } from 'react-hook-form';

import RMAComponent from 'Components/Service/RMA';
import download from 'Utils/download';
import { API_COUNTRIES, API_COUNTIES, API_RMA, COUNTRIES } from 'Constants';
import { UserContext } from 'Contexts/UserContext';
import useFetch from 'Hooks/useFetch';
import useRouter from 'Hooks/useRouter';
import useAsyncError from 'Hooks/useAsyncError';
import useNumberUtils from 'Hooks/useNumberUtils';

const countryId = 192; // Romania
const COUNTIES = 'counties';

const getSanitizedQuery = query => {
  const sanitizedQuery = { ...(query || {}) };
  const page = Number(sanitizedQuery.page);

  if ('page' in sanitizedQuery && (!Number.isInteger(page) || page < 1)) {
    delete sanitizedQuery.page;
  }

  return sanitizedQuery;
};

const defaultValues = {
  name: '',
  productName: '',
  defect: '',
  county: null,
  city: null,
  address: '',
  phone: '',
  postalCode: '',
  weight: '',
  insuredValue: '',
};

const RMA = memo(() => {
  const [requests, setRequests] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [isActionsOpen, setIsActionOpen] = useState(false);
  const [counties, setCounties] = useState(null);
  const [cities, setCities] = useState(null);
  const [isRequestPending, setIsRequestPending] = useState(false);
  const [requestsById, setRequestsById] = useState({});
  const [requestDetailsLoading, setRequestDetailsLoading] = useState({});
  const [postError, setPostError] = useState(null);

  const fetcher = useFetch();
  const { t } = useTranslation();
  const { cache } = useSWRConfig();
  const { location, query } = useRouter();
  const throwError = useAsyncError();
  const { formatDate } = useNumberUtils();
  const {
    formState: { errors: rhfErrors },
    register,
    control,
    reset,
    watch,
    getValues,
    setValue,
    trigger,
  } = useForm({
    defaultValues,
    mode: 'onChange'
  });
  const { email, loggedIn } = useContextSelector(UserContext, ({ data }) => ({
    email: data?.email,
    loggedIn: data?.loggedIn
  }));

  const countyValue = watch('county');
  const cityValue = watch('city');
  const required = t('errors.fieldBlank');
  const minLength = value => ({ value, message: t('errors.fieldMinLength', { minLength: value }) });
  const maxLength = value => ({ value, message: t('errors.fieldMaxLength', { maxLength: value }) });
  const pattern = value => ({ value, message: t('errors.filedInvalidFormat') });
  const minValue = value => ({ value, message: t('errors.minValue', { minValue: value }) });
  const maxValue = value => ({ value, message: t('errors.maxValue', { maxValue: value }) });

  const validators = {
    name: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(60),
    },
    productName: {
      required,
      minLength: minLength(1),
      maxLength: maxLength(512),
    },
    defect: {
      maxLength: maxLength(255),
    },
    county: {
      required,
    },
    city: {
      required,
    },
    address: {
      required,
      minLength: minLength(10),
      maxLength: maxLength(100),
    },
    phone: {
      required,
      pattern: pattern(COUNTRIES[countryId].phoneNumberRegex),
    },
    postalCode: {
      required,
      pattern: pattern(COUNTRIES[countryId].zipCodeRegex),
      validate: {
        district: value => {
          if (countyValue && countyValue?.erpCountyId === '10' && cityValue) {
            // Extract the last 2 digits from cityValue
            const lastTwoDigitsCityValue = String(cityValue?.id).slice(-2);

            // Extract the first 2 digits from zipCode
            const firstTwoDigitsZipCode = String(value).slice(0, 2);

            return lastTwoDigitsCityValue === firstTwoDigitsZipCode || t('errors.postalCodeDistrict', { district: lastTwoDigitsCityValue });
          }
          return true;
        }
      }
    },
    weight: {
      min: minValue(0.01),
      max: maxValue(1000),
    },
    insuredValue: {
      required,
      min: minValue(0.01),
      max: maxValue(1000000)
    }
  };

  const getCounties = useCallback(
    debounce(() => fetcher(`${ API_COUNTRIES }/${ countryId }/${ COUNTIES }`), 250, { leading: true, trailing: false }),
    []
  );
  const getCities = useCallback(
    debounce(fetcher, 250, { leading: true, trailing: false }),
    []
  );

  const getRequests = async () => {
    setRequestsLoading(true);
    let path = API_RMA;
    const sanitizedQuery = getSanitizedQuery(query);

    if (Object.keys(sanitizedQuery).length) {
      path += `?${ stringify(sanitizedQuery) }`;
    }

    fetcher(path).then(res => {
      const resPagination = {
        currentPage: res?.currentPage,
        recordsPerPage: res?.recordsPerPage,
        totalPages: res?.totalPages,
        totalRecords: res?.totalRecords
      };

      const mappedRequests = (res?.items || []).map(item => ({
        ...item,
        status: item.status.name,
        awbTrackingUrl: {
          awbUrl: item?.awbTrackingUrl,
          awbId: item?.awb,
        },
        createdAt: formatDate(item.createdAt),
        actions: {
          id: item.id,
          awbId: item?.awb,
        }
      }));
      setRequests(mappedRequests);
      setPagination(resPagination);
    }).catch(err => throwError(err))
      .finally(() => setRequestsLoading(false));
  };

  // Get RMA Requests on page load
  useEffect(() => {
    getRequests();
  }, [location.pathname, query]);

  // Get counties on load
  useEffect(() => {
    if (!counties && loggedIn) {
      getCounties().then(data => {
        if (data) {
          setCounties(data.map(i => ({ ...i, label: i.name })));
        }
      });
    }
  }, [email, loggedIn]);

  // On county select, populate cities
  useEffect(() => {
    const selectedCounty = countyValue;
    if (selectedCounty) {
      const county = (counties || []).find(c => c.id === selectedCounty.id);
      const key = `${ API_COUNTRIES }/${ countryId }/${ COUNTIES }/${ county.erpCountyId }/cities`;
      if (!cache.has(key)) {
        setCities([]);
        getCities(key).then(data => {
          const mappedCities = (data || []).map(i => ({ ...i, label: i.name }));
          cache.set(key, mappedCities);
          setCities(mappedCities);
        });
      } else {
        const cachedCities = cache.get(key);
        setCities(cachedCities);
      }
    }
  }, [countyValue, counties]);

  const handleOnSetCounty = ({ selectedItem }) => {
    setValue('county', selectedItem);
    setCities(
      selectedItem?.id
        ? cache.get(`${ API_COUNTIES }/${ selectedItem.id }/cities`) || []
        : null
    );
    setValue('city', null);
  };

  const handleCancelAddingRMA = () => {
    reset(defaultValues);
    setIsActionOpen(false);
    setPostError(null);
  };

  const handleSubmit = async () => {
    const isValid = await trigger();
    if (!isValid) {
      // Stop execution if any errors
      return;
    }

    const {
      name,
      productName,
      defect,
      county,
      city,
      address,
      phone,
      postalCode,
      weight,
      insuredValue
    } = getValues();

    const data = {
      contactPersonName: name,
      productName,
      defect,
      countyId: county?.id,
      cityId: city?.id,
      address,
      contactPersonPhone: phone,
      postalCode,
      weight,
      insuredValue,
    };

    setIsRequestPending(true);

    await fetcher(API_RMA, {
      method: 'POST',
      body: JSON.stringify(data),
    }).then(res => {
      const resRequestById = {
        name: res?.contactPersonName,
        productName: res?.productName,
        defect: res?.defect,
        county: res?.county?.name,
        city: res?.city?.name,
        phone: res?.contactPersonPhone,
        address: res?.address,
        postalCode: res?.postalCode,
        weight: res?.weight,
        insuredValue: res?.insuredValue,
      };

      setRequestsById(obj => ({ ...obj, [res?.id]: resRequestById }));
      getRequests();

      setPostError(null);
      setIsActionOpen(false);
      reset(defaultValues);
    }).catch(err => setPostError(err.info.errors.map(i => i?.message).join('\r\n')))
      .finally(() => {
        setIsRequestPending(false);
      });
  };

  const handleRMAById = async id => {
    if (id in requestsById || requestDetailsLoading[id]) return;

    setRequestDetailsLoading(loading => ({ ...loading, [id]: true }));
    fetcher(`${API_RMA}/${id}`).then(res => {
      const resRequest = {
        name: res?.contactPersonName,
        productName: res?.productName,
        defect: res?.defect,
        county: res?.county?.name,
        city: res?.city?.name,
        phone: res?.contactPersonPhone,
        address: res?.address,
        postalCode: res?.postalCode,
        weight: res?.weight,
        insuredValue: res?.insuredValue,
      };
      setRequestsById(obj => ({ ...obj, [id]: resRequest }));
    }).catch(err => throwError(err))
      .finally(() => setRequestDetailsLoading(loading => ({ ...loading, [id]: false })));
  };

  return (
    <RMAComponent
      requests={ requests || [] }
      pagination={ pagination }
      requestsLoading={ requestsLoading }
      isActionsOpen={ isActionsOpen }
      setIsActionOpen={ setIsActionOpen }
      counties={ counties }
      handleSelectedCounty={ handleOnSetCounty }
      cities={ cities }
      loadingCities={ Array.isArray(cities) && cities?.length === 0 }
      handleSubmit={ handleSubmit }
      isRequestPending={ isRequestPending }
      handleRMAById={ handleRMAById }
      requestsById={ requestsById }
      requestDetailsLoading={ requestDetailsLoading }
      postError={ postError }
      downloadAWB={ awbId => download(`${ API_RMA }/${ awbId }/awb`) }
      handleCancelAddingRMA={ handleCancelAddingRMA }
      selectedCounty={ countyValue }
      validators={ validators }
      register={ register }
      rhfErrors={ rhfErrors }
      setValue={ setValue }
      control={ control }
    />
  );
});

RMA.displayName = 'RMA';

export default RMA;
